import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { cn } from './lib/utils';
import { Button, ButtonArrow } from './button';
import {
  Command,
  CommandCheck,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { ScrollArea } from './scroll-area';
import { Skeleton } from './skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';
import type { AIModel } from '@marine/contracts/types/ai.types';

interface AiModelSelectProps {
  label: string;
  value: string | null;
  onChange: (value: string) => void;
  filterVision?: boolean;
  models?: AIModel[];
  isLoading?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

function formatContext(len: number | null): string {
  if (!len) return '';
  if (len >= 1000000) return `${(len / 1000000).toFixed(0)}M ctx`;
  if (len >= 1000) return `${(len / 1000).toFixed(0)}K ctx`;
  return `${len} ctx`;
}

function formatPricing(pricing: AIModel['pricing']): string {
  if (!pricing) return '';
  return `$${pricing.prompt}/M in · $${pricing.completion}/M out`;
}

export function AiModelSelect({
  label,
  value,
  onChange,
  filterVision = false,
  models,
  isLoading = false,
  onRefresh,
  isRefreshing = false,
}: AiModelSelectProps) {
  const [open, setOpen] = useState(false);

  const filteredModels = useMemo(
    () => (models ? (filterVision ? models.filter((m) => m.supports_vision) : models) : []),
    [models, filterVision],
  );

  const hasModels = filteredModels.length > 0;

  const selectedModel = useMemo(
    () => (value ? filteredModels.find((m) => m.id === value) : null),
    [value, filteredModels],
  );

  const selectedDetails = useMemo(() => {
    if (!selectedModel) return null;
    return [formatContext(selectedModel.context_length), formatPricing(selectedModel.pricing)]
      .filter(Boolean)
      .join(' · ');
  }, [selectedModel]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium">{label}</label>
        {onRefresh && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                mode="icon"
                onClick={onRefresh}
                disabled={isRefreshing}
              >
                <RefreshCw
                  className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                  role="img"
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Refresh model list from OpenRouter</TooltipContent>
          </Tooltip>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-8.5 w-full" />
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              mode="input"
              placeholder={!selectedModel}
              aria-expanded={open}
              className="w-full justify-between h-auto min-h-9 py-1.5"
              disabled={!hasModels}
            >
              {selectedModel ? (
                <span className="flex flex-col items-start min-w-0 text-left">
                  <span className="truncate">{selectedModel.name}</span>
                  {selectedDetails && (
                    <span className="text-[10px] text-muted-foreground truncate">
                      {selectedDetails}
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {hasModels ? 'Select a model...' : 'No models available'}
                </span>
              )}
              <ButtonArrow />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search models..." />
              <CommandList>
                <ScrollArea viewportClassName="max-h-[300px] [&>div]:block!">
                  <CommandEmpty>No models found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="Use default global model"
                      onSelect={() => {
                        onChange('__none__');
                        setOpen(false);
                      }}
                    >
                      <span className="text-muted-foreground">Use default (global model)</span>
                      <CommandCheck
                        className={cn(!value || value === '__none__' ? 'opacity-100' : 'opacity-0')}
                      />
                    </CommandItem>
                    {filteredModels.map((model) => {
                      const details = [
                        formatContext(model.context_length),
                        formatPricing(model.pricing),
                      ]
                        .filter(Boolean)
                        .join(' · ');

                      return (
                        <CommandItem
                          key={model.id}
                          value={`${model.name} ${model.id}`}
                          onSelect={() => {
                            onChange(model.id);
                            setOpen(false);
                          }}
                        >
                          <span className="flex flex-col min-w-0">
                            <span className="truncate">{model.name}</span>
                            {details && (
                              <span className="text-[10px] text-muted-foreground truncate">
                                {details}
                              </span>
                            )}
                          </span>
                          <CommandCheck
                            className={cn(value === model.id ? 'opacity-100' : 'opacity-0')}
                          />
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </ScrollArea>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
